import {CommonModule} from '@angular/common';
import {ModuleWithProviders, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
// app modules
import {ApiModule} from '../api/api.module';
import {CommonsModule} from '../commons/commons.module';
import {I18nModule} from '../i18n/i18n.module';
import {SharedModule} from '../shared/shared.module';
import {AuthModule} from 'auth-commons';
// app view modules
import {LoginComponent} from './login/login.component';
import {ChangePwdComponent} from './change-pwd/change-pwd.component';
import {RecoverPwdComponent} from './recover-pwd/recover-pwd.component';
import {AcceptClausesComponent} from './accept-clauses/accept-clauses.component';
import {ActivateTotpComponent} from './activate-totp/activate-totp.component';
import {ForbiddenComponent} from './forbidden/forbidden.component';
import {ECNotificationsComponent} from './ec-notifications/ec-notifications.component';
import {ECMembersComponent} from './ec-members/ec-members.component';
import {ConfigConnectivityComponent} from './connectivity/config-connectivity.component';
import {PermAdminPanelComponent} from './perm-admin/perm-admin-panel.component';
import {PermAdminResourcesComponent} from './perm-admin/perm-admin-resources/perm-admin-resources.component';
import {PermAdminConnectorsComponent} from './perm-admin/perm-admin-connectors/perm-admin-connectors.component';
import {PermAdminUsersComponent} from './perm-admin/perm-admin-users/perm-admin-users.component';
import {NewECMemberComponent} from './ec-members/new-member/new-ec-member.component';
import {ECMemberDetailsComponent} from './ec-members/member-details/ec-member-details.component';
import {
    UsersPermissionAssignmentsComponent
} from './perm-admin/users-permission-assignments/users-permission-assignments.component';
import {NewConnectorComponent} from './connectivity/new-connector/new-connector.component';
import {PollingPolicyComponent} from './connectivity/polling-policy/polling-policy.component';
import {ModbusConfigComponent} from './connectivity/modbus-config/modbus-config.component';
import {BacnetConfigComponent} from './connectivity/bacnet-config/bacnet-config.component';
import {Iec61850ConfigComponent} from './connectivity/iec61850-config/iec61850-config.component';
import {MergeConfigComponent} from './connectivity/merge-config/merge-config.component';
import {MeteoConfigComponent} from './connectivity/meteo-config/meteo-config.component';
import {MqttConfigComponent} from './connectivity/mqtt-config/mqtt-config.component';
import {ExpressionEditorComponent} from './connectivity/expression-editor/expression-editor.component';
import {UserAdminPanelComponent} from './user-admin-panel/user-admin-panel.component';
import {UserProfileComponent} from './user-profile/user-profile.component';
import {PermissionAssignmentsComponent} from './perm-admin/permission-assignments/permission-assignments.component';
import {PermissionListComponent} from './perm-admin/permission-list/permission-list.component';
import {
    NewPermissionAssignmentComponent
} from './perm-admin/new-permission-assignment/new-permission-assignment.component';
import {SubjectPermissionsComponent} from './perm-admin/subject-permissions/subject-permissions.component';

import {
    ConnectorTypePipe,
    IEC61850DataTypePipe,
    IEC61850RefTypePipe,
    MergeDeviceModePipe,
    MeteoMeasureTypePipe,
    MeteoSourceTypePipe,
    ModbusDataTypePipe,
    ModbusFrameTypePipe,
    ModbusRegTypePipe,
    ModbusWordTypePipe,
    MqttAuthTypePipe,
    MqttChain2GateMeasureTypePipe,
    MqttMiniGatewayMeasureTypePipe,
    MqttDeviceTypePipe,
    MqttUrbanDatasetMeasureTypePipe,
    MqttDinoPlusMeasureTypePipe,
    MqttDuelMeasureTypePipe,
    PollingPolicyPipe,
} from './connectivity/connector.pipe';
import {PermissionDescriptionPipe, PermissionIconPipe, PermissionNamePipe} from './perm-admin/permission.pipe';
import {ECEconomicsComponent} from './ec-economics/ec-economics.component';
import {AuthenticationResolverService} from './authentication-resolver.service';
import {ECResolverService} from './ec-container/ec-resolver.service';
import {ConfigurationConnectivityResolverService} from './connectivity/configuration-connectivity-resolver.service';
import {PermissionManagementService} from './perm-admin/permission-management.service';
import {
    PermAdminResourcesResolverService
} from './perm-admin/perm-admin-resources/perm-admin-resources-resolver.service';
import {
    PermAdminConnectorsResolverService
} from './perm-admin/perm-admin-connectors/perm-admin-connectors-resolver.service';
import {PermAdminUsersResolverService} from './perm-admin/perm-admin-users/perm-admin-users-resolver.service';
import {
    UsersPermissionAssignmentsResolverService
} from './perm-admin/users-permission-assignments/users-permission-assignments-resolver.service';
import {ECEconomicsAggregatedComponent} from './ec-economics/economics-aggregated/ec-economics-aggregated.component';
import {
    ECEconomicsRedistributedComponent
} from './ec-economics/economics-redistributed/ec-economics-redistributed.component';
import {ECGseDataComponent} from './ec-economics/economics-aggregated/gse-data/ec-gse-data.component';
import {ECCostsComponent} from './ec-economics/economics-aggregated/costs/ec-costs.component';
import {ECEnergyDataComponent} from './ec-economics/economics-aggregated/energy-data/ec-energy-data.component';
import {ECPlantsComponent} from './ec-economics/plants/ec-plants.component';
import {ECMarketComponent} from './ec-economics/market/ec-market.component';
import {ECPlantsDetailsComponent} from './ec-economics/plants/plants-details/ec-plants-details.component';
import {EcBillingFilterComponent} from './ec-economics/billing-filter/ec-billing-filter.component';
import {ECAlgorithmComponent} from './ec-economics/economics-redistributed/algorithm/ec-algorithm.component';
import {DevDashboardComponent} from './dev-dashboard/dev-dashboard.component';
import {DevDashboardResolverService} from './dev-dashboard/dev-dashboard-resolver.service';
import {CustomerCareComponent} from './customer-care/customer-care.component';
import {SpinnerComponent} from '../shared/spinner/spinner.component';
import {CustomerCareResolverService} from './customer-care/customer-care-resolver.service';
import {ECContainerComponent} from './ec-container/ec-container.component';
import {ECDashboardComponent} from './ec-dashboard/ec-dashboard.component';
import {ECMeasuresComponent} from './ec-measures/ec-measures.component';
import {ECWeatherForecastComponent} from './ec-weather-forecast/ec-weather-forecast.component';
import {ECResourcesComponent} from './ec-resources/ec-resources.component';
import {ECPlanningComponent} from './ec-planning/ec-planning.component';
import {ForecastComponent} from './forecast/forecast.component';
import {CapSelectorComponent} from './ec-economics/economics-redistributed/cap-selector/cap-selector.component';
import {MembershipFeeComponent} from './ec-economics/economics-aggregated/membership-fee/membership-fee.component';
import {EcEditorComponent} from './ec-editor/ec-editor.component';
import {EcCreatorComponent} from './ec-creator/ec-creator.component';
import {CommunityPlanningDataComponent} from './ec-planning/community-planning-data/community-planning-data.component';
import {ForecastDetailsNewComponent} from './forecast/forecast-details-new/forecast-details-new.component';
import {MeasuresUploadComponent} from './ec-measures/measures-upload/measures-upload.component';
import {EcPodCreatorWizardComponent} from './ec-resources/ec-pod-creator-wizard/ec-pod-creator-wizard.component';
import {MembersMassiveImportComponent} from './ec-members/members-massive-import/members-massive-import.component';
import {ECRepresentativesComponent} from './ec-representatives/ec-representatives.component';
import {ECRepresentativesResolverService} from './ec-representatives/ec-representatives-resolver.service';
import {NewRepresentativeComponent} from './ec-representatives/new-representative/new-representative.component';
import {EditRepresentativeComponent} from './ec-representatives/edit-representative/edit-representative.component';
import {
    RepresentativeDetailsComponent
} from './ec-representatives/representative-details/representative-details.component';
import {MatStepperModule} from '@angular/material/stepper';
import {ActivationDateDialogComponent} from './ec-creator/activation-date-dialog/activation-date-dialog.component';
import {ECDocumentsComponent} from './ec-documents/ec-documents.component';
import {ActivationDateComponent} from './ec-resources/ec-pod-creator-wizard/activation-date/activation-date.component';
import {FilterBarComponent} from './ec-economics/filter-bar/filter-bar.component';
import {
    EcEconomicsTotalsSummaryComponent
} from './ec-economics/economics-totals-summary/ec-economics-totals-summary.component';
import {NgApexchartsModule} from 'ng-apexcharts';
import {EcEconomicsSubjectsSummaryComponent} from './ec-economics/economics-subjects-summary/ec-economics-subjects-summary.component';
import {EcSelectDateComponent} from './ec-economics/economics-aggregated/ec-select-date/ec-select-date.component';
import {SetTipComponent} from './ec-economics/plants/set-tip/set-tip.component';
import {SetReiComponent} from './ec-economics/plants/set-rei/set-rei.component';
import {SetBilledDataComponent} from './ec-economics/plants/plants-details/set-billed-data/set-billed-data.component';
import { EconomicsTotalsPlantsComponent } from './ec-economics/economics-totals-plants/economics-totals-plants.component';
import { EconomicsMemberPlantsComponent } from './ec-economics/economics-member-plants/economics-member-plants.component';
import { EconomicsTotalsCommunityComponent } from './ec-economics/economics-totals-community/economics-totals-community.component';
import { EconomicsMemberPodsComponent } from './ec-economics/economics-member-pods/economics-member-pods.component';
import { SetCacvComponent } from './ec-economics/plants/set-cacv/set-cacv.component';
import { GseUploadComponent } from './ec-measures/gse-upload/gse-upload.component';
import { ConsolidationInfoBarComponent } from './ec-economics/consolidation-info-bar/consolidation-info-bar.component';
import { GseDeleteComponent } from './ec-measures/gse-delete/gse-delete.component';
import { GseErrorsComponent } from './ec-measures/gse-errors/gse-errors.component';
import { StrategyComponent } from './ec-economics/economics-aggregated/strategy/strategy.component';

@NgModule({
    declarations: [
        ForbiddenComponent,
        LoginComponent,
        ChangePwdComponent,
        RecoverPwdComponent,
        AcceptClausesComponent,
        ActivateTotpComponent,
        ECContainerComponent,
        ECDashboardComponent,
        ECResourcesComponent,
        ECMeasuresComponent,
        ECWeatherForecastComponent,
        ECDocumentsComponent,
        ECEconomicsComponent,
        ECEconomicsAggregatedComponent,
        ECGseDataComponent,
        EcSelectDateComponent,
        ECCostsComponent,
        ECEnergyDataComponent,
        ECPlantsComponent,
        ECPlantsDetailsComponent,
        ECMarketComponent,
        ECEconomicsRedistributedComponent,
        EcEconomicsSubjectsSummaryComponent,
        ECAlgorithmComponent,
        EcBillingFilterComponent,
        FilterBarComponent,
        ECNotificationsComponent,
        ECMembersComponent,
        ConfigConnectivityComponent,
        PermAdminPanelComponent,
        PermAdminResourcesComponent,
        PermAdminConnectorsComponent,
        PermAdminUsersComponent,
        NewECMemberComponent,
        ECMemberDetailsComponent,
        UsersPermissionAssignmentsComponent,
        NewConnectorComponent,
        PollingPolicyComponent,
        ModbusConfigComponent,
        BacnetConfigComponent,
        Iec61850ConfigComponent,
        MergeConfigComponent,
        MeteoConfigComponent,
        MqttConfigComponent,
        ExpressionEditorComponent,
        UserProfileComponent,
        UserAdminPanelComponent,
        PermissionAssignmentsComponent,
        PermissionListComponent,
        NewPermissionAssignmentComponent,
        SubjectPermissionsComponent,
        ECPlanningComponent,
        ForecastComponent,
        ConnectorTypePipe,
        PollingPolicyPipe,
        ModbusFrameTypePipe,
        ModbusDataTypePipe,
        ModbusRegTypePipe,
        ModbusWordTypePipe,
        IEC61850DataTypePipe,
        IEC61850RefTypePipe,
        MergeDeviceModePipe,
        MeteoSourceTypePipe,
        MeteoMeasureTypePipe,
        MqttAuthTypePipe,
        MqttDeviceTypePipe,
        MqttChain2GateMeasureTypePipe,
        MqttDinoPlusMeasureTypePipe,
        MqttDuelMeasureTypePipe,
        MqttMiniGatewayMeasureTypePipe,
        MqttUrbanDatasetMeasureTypePipe,
        PermissionNamePipe,
        PermissionIconPipe,
        PermissionDescriptionPipe,
        DevDashboardComponent,
        ECRepresentativesComponent,
        CustomerCareComponent,
        SpinnerComponent,
        CapSelectorComponent,
        CapSelectorComponent,
        MembershipFeeComponent,
        EcEditorComponent,
        EcCreatorComponent,
        CommunityPlanningDataComponent,
        ForecastDetailsNewComponent,
        MeasuresUploadComponent,
        EcPodCreatorWizardComponent,
        NewRepresentativeComponent,
        EditRepresentativeComponent,
        RepresentativeDetailsComponent,
        MeasuresUploadComponent,
        ActivationDateDialogComponent,
        MembersMassiveImportComponent,
        ActivationDateComponent,
        EcEconomicsTotalsSummaryComponent,
        SetTipComponent,
        SetReiComponent,
        SetBilledDataComponent,
        EcEconomicsTotalsSummaryComponent,
        EconomicsTotalsPlantsComponent,
        EconomicsMemberPlantsComponent,
        EconomicsTotalsCommunityComponent,
        EconomicsMemberPodsComponent,
        SetCacvComponent,
        GseUploadComponent,
        ConsolidationInfoBarComponent,
        GseDeleteComponent,
        GseErrorsComponent,
        StrategyComponent
    ],
    imports: [
        // angular
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        // application
        ApiModule,
        AuthModule,
        CommonsModule,
        I18nModule,
        SharedModule,
        MatStepperModule,
        NgApexchartsModule
    ],
    exports: [
        LoginComponent,
        ChangePwdComponent,
        RecoverPwdComponent,
        AcceptClausesComponent,
        ActivateTotpComponent,
        ForbiddenComponent,
        ECContainerComponent,
        ECDashboardComponent,
        ECResourcesComponent,
        ECMeasuresComponent,
        ECWeatherForecastComponent,
        ECDocumentsComponent,
        ECMembersComponent,
        ECEconomicsComponent,
        ECPlanningComponent,
        ForecastComponent,
        ECNotificationsComponent,
        PermAdminPanelComponent,
        UserProfileComponent,
        UserAdminPanelComponent,
        DevDashboardComponent,
        ECRepresentativesComponent,
        EcEditorComponent,
        EcCreatorComponent,
        CommunityPlanningDataComponent,
        ForecastDetailsNewComponent
    ]
})
export class ViewsModule {

    /**
     * Configures the ViewsModule.
     *
     * @return The module with the providers
     */
    static forRoot(): ModuleWithProviders<ViewsModule> {
        return {
            ngModule: ViewsModule,
            providers: [
                AuthenticationResolverService,
                ECResolverService,
                ConfigurationConnectivityResolverService,
                PermissionManagementService,
                PermAdminResourcesResolverService,
                PermAdminConnectorsResolverService,
                PermAdminUsersResolverService,
                UsersPermissionAssignmentsResolverService,
                DevDashboardResolverService,
                ECRepresentativesResolverService,
                CustomerCareResolverService
            ]
        };
    }
}
